import { useCachedMembers } from '@easy-expense/data-firestore-client';
import { ReportWithData } from '@easy-expense/data-schema-v2';
import { getTranslation, useIntlStore } from '@easy-expense/intl-client';
import { Icon } from '@easy-expense/ui-shared-components';
import { theme } from '@easy-expense/ui-theme';
import { Layout, OpenSans, Spacer } from '@easy-expense/ui-web-core';
import {
  getReportDisplayStatus,
  getReportStatusLabel,
  getReportStatusLabelColor,
} from '@easy-expense/utils-client';
import { dateFormatSkeleton, getUserDisplayName, hueToColor } from '@easy-expense/utils-shared';
import _ from 'lodash';
import {
  MRT_GlobalFilterTextField,
  MRT_ShowHideColumnsButton,
  MRT_ToggleFiltersButton,
  MaterialReactTable,
  useMaterialReactTable,
  type MRT_ColumnDef,
} from 'material-react-table';
import React from 'react';
import { useNavigate } from 'react-router-dom';

import { ReportDownloadColumnCell } from './ReportDownloadColumnCell.component';
import { useReportsRows } from './useReportTableRows';
import { usePersistentState } from '../../hooks/usePersistantState';
import {
  getResumeRow,
  resolveResumeRowPage,
  ResumeRowList,
  setResumeRow,
} from '../../lib/resumeRow';
import { StatusBadge } from '../Shared/StatusBadge.component';
import { TableTextSize } from '../Shared/TableTextSize';

interface ReportMaterialTableProps {
  reportKeys?: string[];
  header?: React.ReactNode;
  /**
   * Opts this table into tracking a Resume Row under the given list, so it re-opens on
   * the page holding the row the user was last looking at. Omit for the approvals queue,
   * which always starts at the first page.
   */
  resumeRowList?: ResumeRowList;
}

export const ReportMaterialTable: React.FC<React.PropsWithChildren<ReportMaterialTableProps>> = ({
  reportKeys,
  header,
  resumeRowList,
}) => {
  const navigate = useNavigate();
  const allReportRows = useReportsRows();
  const reportRows = React.useMemo(
    () =>
      reportKeys ? allReportRows.filter((row) => reportKeys.includes(row.key)) : allReportRows,
    [allReportRows, reportKeys],
  );
  const members = useCachedMembers();
  const { formatCurrency, formatDate } = useIntlStore();

  const [pageIndex, setPageIndex] = React.useState(0);
  const [reportPageSize, setReportPageSize] = usePersistentState<number>('reportPageSize');
  const pageSize = reportPageSize ?? 25;

  // The rows in the order they are displayed, kept in a ref so the pagination handler can
  // name the row a page starts on without re-creating the handler every time reports change.
  const sortedRowKeysRef = React.useRef<string[]>([]);
  // Flipped once the Resume Row has been restored, or once the user has moved somewhere of
  // their own accord and the restore has been overtaken.
  const resumeRestoreDone = React.useRef(false);
  // Read during the first render so the restore targets the row the user arrived with,
  // rather than one a later interaction has re-pointed the Resume Row at.
  const rowToResumeOn = React.useRef(resumeRowList ? getResumeRow(resumeRowList) : undefined);

  function onRowClick(row: ReportWithData) {
    navigate(`/report/${row.key}`);
  }

  const columns = React.useMemo<MRT_ColumnDef<ReportWithData>[]>(
    () => [
      {
        id: 'name',
        accessorFn: (row) => row.name,
        header: getTranslation('Report Name'),
        Header: () => (
          <OpenSans.Secondary size={TableTextSize.xs}>
            {getTranslation('Report Name')}
          </OpenSans.Secondary>
        ),
        size: 300,
        Cell: ({ row }) => {
          const name = row.original.name;
          const color = row.original.color;
          return (
            <Layout.Row style={{ width: 300, height: 30 }} align>
              <Layout.Column
                style={{ width: 8, backgroundColor: hueToColor(color), height: '100%' }}
                radius={4}
              />
              <Spacer.Horizontal size={12} />
              <OpenSans.Primary size={TableTextSize.xs} weight="bold-700">
                {name}
              </OpenSans.Primary>
            </Layout.Row>
          );
        },
      },
      {
        accessorFn: (row) => {
          const sharedWith = row.shared
            ? getTranslation('Everyone')
            : getUserDisplayName(members.find((m) => m.key === row.createdBy));
          return sharedWith;
        },
        header: getTranslation('Shared With'),
        Header: () => (
          <OpenSans.Secondary size={TableTextSize.xs}>
            {getTranslation('Shared With')}
          </OpenSans.Secondary>
        ),
        size: 100,
        Cell: ({ row }) => {
          const sharedWith = row.original.shared
            ? getTranslation('Everyone')
            : getUserDisplayName(members.find((m) => m.key === row.original.createdBy));

          return (
            <Layout.Column>
              <OpenSans.Primary
                size={TableTextSize.xs}
                style={{
                  width: 100,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: 'inline-block',
                  whiteSpace: 'nowrap',
                  textAlign: 'left',
                }}
              >
                {sharedWith}
              </OpenSans.Primary>
            </Layout.Column>
          );
        },
      },
      {
        accessorFn: (row) => row.clientValue?.name,
        accessorKey: 'clientValue.value.name',
        header: getTranslation('Client'),
        Header: () => (
          <OpenSans.Secondary size={TableTextSize.xs}>
            {getTranslation('Client')}
          </OpenSans.Secondary>
        ),
        size: 100,
        Cell: ({ row }) => {
          return (
            <Layout.Column>
              <OpenSans.Primary
                size={TableTextSize.xs}
                style={{
                  width: 100,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: 'inline-block',
                  whiteSpace: 'nowrap',
                  textAlign: 'left',
                }}
              >
                {row.original.clientValue?.name}
              </OpenSans.Primary>
            </Layout.Column>
          );
        },
      },
      {
        accessorFn: (row) => getReportDisplayStatus(row),
        name: 'status',
        id: 'status',
        header: getTranslation('Status'),
        Header: () => (
          <OpenSans.Secondary size={TableTextSize.xs}>
            {getTranslation('Status')}
          </OpenSans.Secondary>
        ),
        size: 100,
        Cell: ({ row }) => {
          const status = getReportDisplayStatus(row.original);

          return (
            <Layout.Row>
              <StatusBadge
                color={getReportStatusLabelColor(status)}
                text={getReportStatusLabel(status)}
              />
            </Layout.Row>
          );
        },
      },
      {
        accessorFn: (row) => new Date(row.updatedAt),
        id: 'updatedAt',
        filterVariant: 'date-range',
        filterFn: 'betweenInclusive',
        sortingFn: 'datetime',
        header: getTranslation('Updated On'),
        Header: () => (
          <OpenSans.Secondary size={TableTextSize.xs}>
            {getTranslation('Update On')}
          </OpenSans.Secondary>
        ),
        maxSize: 10,
        Cell: ({ row }) => {
          const date = row.original.updatedAt;
          return (
            <Layout.Column style={{ width: 60 }}>
              <OpenSans.Secondary size={TableTextSize.xs}>
                {formatDate(new Date(date), {
                  skeleton: dateFormatSkeleton.abbreviatedNoYear,
                })}
              </OpenSans.Secondary>
            </Layout.Column>
          );
        },
      },
      {
        accessorFn: (row) =>
          row.reportExpenses.length + row.reportIncomes.length + row.trips.length,
        header: getTranslation('Items'),
        Header: () => (
          <OpenSans.Secondary size={TableTextSize.xs} style={{ paddingLeft: 6 }}>
            {getTranslation('Items')}
          </OpenSans.Secondary>
        ),
        muiTableHeadCellProps: {
          align: 'right',
        },
        size: 100,
        Cell: ({ row }) => {
          const items =
            row.original.reportExpenses.length +
            row.original.reportIncomes.length +
            row.original.trips.length;
          return (
            <Layout.Column align="flex-end">
              <OpenSans.Primary
                size={TableTextSize.xs}
                style={{
                  width: 50,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: 'inline-block',
                  whiteSpace: 'nowrap',
                  textAlign: 'right',
                }}
              >
                {items}
              </OpenSans.Primary>
            </Layout.Column>
          );
        },
      },
      {
        accessorFn: (row) => row.total,
        accessorKey: 'clientValue.total',
        header: getTranslation('Total'),
        Header: () => (
          <OpenSans.Secondary size={TableTextSize.xs} style={{ paddingLeft: 6 }}>
            {getTranslation('Total')}
          </OpenSans.Secondary>
        ),
        muiTableHeadCellProps: {
          align: 'right',
        },
        size: 100,
        Cell: ({ row }) => {
          return (
            <Layout.Column align="flex-end">
              <OpenSans.Primary
                size={TableTextSize.xs}
                style={{
                  width: 100,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: 'inline-block',
                  whiteSpace: 'nowrap',
                  textAlign: 'right',
                }}
                weight="bold-700"
              >
                {formatCurrency(row.original.total)}
              </OpenSans.Primary>
            </Layout.Column>
          );
        },
      },
      {
        accessorFn: (row) => row.key,
        name: 'download',
        header: getTranslation('Download'),
        filter: false,
        enableSorting: false,
        enableColumnFilter: false,
        size: 80,
        Header: () => (
          <OpenSans.Secondary size={TableTextSize.xs}>
            {getTranslation('Download')}
          </OpenSans.Secondary>
        ),
        muiTableHeadCellProps: {
          align: 'right',
        },
        Cell: ({ row }) => {
          return (
            <Layout.Column align="flex-end">
              <ReportDownloadColumnCell row={row} />
            </Layout.Column>
          );
        },
      },
    ],
    [],
  );

  const table = useMaterialReactTable({
    columns,
    data: reportRows,
    initialState: {
      showGlobalFilter: true,
      density: 'compact',
      sorting: [
        { desc: true, id: 'status' },
        { desc: true, id: 'updatedAt' },
      ],
    },
    state: {
      pagination: { pageIndex, pageSize },
    },
    // The page index is reset by hand below instead. TanStack resets it whenever the row
    // model is rebuilt, which here happens on any background change to the expenses, incomes
    // or trips the report totals are built from - taking a user reading page two back to the
    // first page, and undoing the Resume Row restore along with it.
    autoResetPageIndex: false,
    onPaginationChange: (updater) => {
      const prev = { pageIndex, pageSize };
      const next = typeof updater === 'function' ? updater(prev) : updater;
      setPageIndex(next.pageIndex);
      setReportPageSize(next.pageSize);
      // A move of the user's own, so the restore has been overtaken: disarm it rather than
      // let a report arriving late pull them off the page they chose. Compared rather than
      // assumed, so that a handler fired with the page it is already on cannot disarm a
      // restore that has not happened yet.
      if (next.pageIndex !== prev.pageIndex || next.pageSize !== prev.pageSize) {
        resumeRestoreDone.current = true;
      }
      // Paging is the only thing that sets the Resume Row: it names the row a page starts on,
      // so the list comes back to that page even when the report the user opened has since
      // moved. Opening a report deliberately records nothing - saving one stamps a new
      // updatedAt, which this table sorts on, so the report itself is the one row guaranteed
      // not to still be where it was. A page with no row behind it yet means the reports are
      // still loading, which is not the user moving anywhere - leave the Resume Row alone.
      const rowStartingPage = sortedRowKeysRef.current[next.pageIndex * next.pageSize];
      if (resumeRowList && rowStartingPage) {
        setResumeRow(resumeRowList, rowStartingPage);
      }
    },
    icons: {
      CloseIcon: () => (
        <Icon
          name="close"
          size={18}
          color={theme.colors.secondary}
          style={{ padding: 0, margin: 0 }}
        />
      ),
      SearchIcon: () => (
        <Icon name="search" size={18} color={theme.colors.secondary} style={{ marginRight: 8 }} />
      ),
      FilterAltIcon: () => <Icon name="funnel" size={18} color={theme.colors.secondary} />,
      ViewColumnIcon: () => <Icon name="build-outline" size={18} color={theme.colors.secondary} />,
    },
    enableColumnActions: false,
    columnFilterDisplayMode: 'subheader',
    getRowId: (row) => row.key,
    enableDensityToggle: false,
    muiTableHeadCellProps: {
      sx: {
        '& .Mui-TableHeadCell-Content': {
          color: theme.colors.secondary,
          fontWeight: 'regular',
        },
      },
    },
    muiTableBodyRowProps: ({ row }) => ({
      onClick: () => onRowClick(row.original),
      sx: {
        cursor: 'pointer',
      },
    }),
    muiTablePaperProps: {
      elevation: 0,
      sx: {
        borderRadius: '0',
        border: '',
      },
    },
    renderTopToolbar: ({ table }) => {
      return (
        <Layout.Row py>
          <MRT_GlobalFilterTextField
            table={table}
            sx={{
              borderColor: theme.colors.inputBorder,
              '& .MuiInputBase-root': {
                backgroundColor: theme.colors.inputBackground,
                borderRadius: 2,
                borderColor: theme.colors.success,
                width: 500,
                paddingRight: 1,
              },
            }}
          />
          <Spacer.Horizontal size={6} />
          <MRT_ShowHideColumnsButton table={table} />
          <Spacer.Horizontal size={6} />

          <MRT_ToggleFiltersButton table={table} />
        </Layout.Row>
      );
    },
  });

  // The filtered and sorted order across all pages, which is what a page number counts
  // against.
  const sortedRowKeys = table.getSortedRowModel().rows.map((row) => row.id);
  const sortedRowKeysSignature = sortedRowKeys.join(',');
  sortedRowKeysRef.current = sortedRowKeys;

  // Open on the page holding the Resume Row. Reports reach a member through two listeners -
  // one for shared reports and one for their own - each replacing the whole list as it
  // resolves, so the first order to arrive need not hold the remembered report yet. Stay
  // armed until it turns up rather than giving up and settling on the first page, which is
  // the very thing this is here to stop.
  React.useEffect(() => {
    if (!resumeRowList || !rowToResumeOn.current || resumeRestoreDone.current) {
      return;
    }
    const resumePage = resolveResumeRowPage(rowToResumeOn.current, sortedRowKeys, pageSize);
    if (resumePage === undefined) {
      return;
    }
    resumeRestoreDone.current = true;
    setPageIndex(resumePage);
  }, [sortedRowKeysSignature, pageSize]);

  // Searching, filtering or re-sorting drops the user back to the first page, which is what
  // TanStack's automatic reset did before it was turned off above. These three stay
  // uncontrolled - controlling them would be only to watch them - so the change is read from
  // the table's own state instead. The first run is the table reporting the state it started
  // with and must not undo the Resume Row restore.
  const { sorting, globalFilter, columnFilters } = table.getState();
  const viewSignature = JSON.stringify([sorting, globalFilter ?? '', columnFilters]);
  const lastViewSignature = React.useRef(viewSignature);
  React.useEffect(() => {
    if (viewSignature === lastViewSignature.current) {
      return;
    }
    lastViewSignature.current = viewSignature;
    resumeRestoreDone.current = true;
    setPageIndex(0);
    // The row that anchored the old page says nothing about the new order, and the new one
    // has not been built yet, so there is no replacement to record. Forgetting it leaves the
    // list re-opening on the first page, which is the page the user is now on.
    if (resumeRowList) {
      setResumeRow(resumeRowList, undefined);
    }
  }, [viewSignature]);

  // Rows can fall away underneath the page being shown - a column filter set from the table's
  // own header, or another member archiving a report. With the automatic reset turned off,
  // bring a page that has run past the end back to the first one so the user never lands on
  // an empty table. A list that is still loading has no pages yet and is left alone.
  const lastPageIndex = Math.max(0, Math.ceil(sortedRowKeys.length / pageSize) - 1);
  React.useEffect(() => {
    if (sortedRowKeys.length > 0 && pageIndex > lastPageIndex) {
      setPageIndex(0);
      if (resumeRowList) {
        setResumeRow(resumeRowList, undefined);
      }
    }
  }, [lastPageIndex, pageIndex, sortedRowKeys.length]);

  return (
    <Layout.Column>
      <Layout.Row>{header}</Layout.Row>
      <MaterialReactTable table={table} />
    </Layout.Column>
  );
};
